import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Input, cx } from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  adminApi,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  type AdminUserRow,
} from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState } from './AdminLayout';

const PAGE_SIZE = 25;

const STATUS_TONE: Record<string, string> = {
  active: 'border-pass-600/40 bg-pass-600/10 text-pass-400',
  suspended: 'border-fail-600/40 bg-fail-600/10 text-fail-400',
  pending_activation: 'border-warn-600/40 bg-warn-600/10 text-warn-400',
  closed: 'border-hull-600 bg-hull-800 text-hull-300',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cx(
        'inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs',
        STATUS_TONE[status] ?? STATUS_TONE.closed,
      )}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

/**
 * The customer directory.
 *
 * Read, plus the one write an operator genuinely needs: suspend and reinstate.
 * There is no edit-the-customer control because the API has no endpoint for one
 * — changing a tenant's own data on their behalf needs a reason and a record
 * before it is worth the blast radius.
 */
export function AdminUsers() {
  usePageTitle('Users — WaveQA Admin');

  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['admin', 'users', submitted, plan, status, page],
    queryFn: () =>
      adminApi.getUsers({
        search: submitted || undefined,
        plan: plan || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const pages = users.data ? Math.max(1, Math.ceil(users.data.total / users.data.page_size)) : 1;

  function applyFilters(next: { plan?: string; status?: string; search?: string }) {
    if (next.plan !== undefined) setPlan(next.plan);
    if (next.status !== undefined) setStatus(next.status);
    if (next.search !== undefined) setSubmitted(next.search);
    // Any filter change invalidates the page number: staying on page 4 of a
    // result set that now has one page shows an empty table.
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Users</h1>
        <p className="mt-1 text-sm text-hull-400">
          {users.data
            ? `${formatCount(users.data.total)} account${users.data.total === 1 ? '' : 's'}`
            : 'Customer accounts'}
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters({ search });
        }}
      >
        <div className="min-w-0 flex-1 sm:max-w-xs">
          <label htmlFor="user-search" className="mb-1.5 block text-xs text-hull-400">
            Search
          </label>
          <Input
            id="user-search"
            type="search"
            placeholder="Email or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div>
          <label htmlFor="user-plan" className="mb-1.5 block text-xs text-hull-400">
            Plan
          </label>
          <select
            id="user-plan"
            value={plan}
            onChange={(event) => applyFilters({ plan: event.target.value })}
            className="rounded-lg border border-hull-600 bg-hull-950 px-3 py-2.5 text-sm text-white"
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
          </select>
        </div>

        <div>
          <label htmlFor="user-status" className="mb-1.5 block text-xs text-hull-400">
            Status
          </label>
          <select
            id="user-status"
            value={status}
            onChange={(event) => applyFilters({ status: event.target.value })}
            className="rounded-lg border border-hull-600 bg-hull-950 px-3 py-2.5 text-sm text-white"
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending_activation">Pending activation</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      <AdminPanelState
        loading={users.isPending}
        error={users.error}
        empty={users.data?.rows.length === 0}
        emptyTitle="No accounts match"
        emptyBody="Try a different search term, or clear the plan and status filters."
      >
        {users.data && users.data.rows.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-card border border-hull-700">
              <table className="w-full min-w-[48rem] text-left text-sm">
                <thead className="bg-hull-800/60 text-xs uppercase tracking-wider text-hull-400">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      User
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Plan
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Promotion
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Joined
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      Revenue
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.data.rows.map((row) => (
                    <tr key={row.id} className="border-t border-hull-800">
                      <td className="px-4 py-3">
                        <p className="font-medium text-white">{row.display_name ?? '—'}</p>
                        <p className="text-xs text-hull-400">{row.email}</p>
                      </td>
                      <td className="px-4 py-3 capitalize text-hull-200">
                        {row.plan ?? <span className="text-hull-500">none</span>}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {row.promotion_ends_at === null ? (
                          <span className="text-xs text-hull-500">—</span>
                        ) : row.promotion_active ? (
                          <span className="text-xs text-pass-400">
                            until {formatDate(row.promotion_ends_at)}
                          </span>
                        ) : (
                          <span
                            className={cx(
                              'text-xs',
                              row.awaiting_payment ? 'text-warn-400' : 'text-hull-400',
                            )}
                          >
                            {row.awaiting_payment ? 'awaiting payment' : 'ended'}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-hull-300">
                        {formatDate(row.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-hull-200">
                        {formatMoney(row.revenue_minor_units, users.data.currency, {
                          exact: true,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setSelected(row.id)}
                          className="whitespace-nowrap text-xs text-surge-400 underline underline-offset-2 hover:text-surge-300"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <nav
                aria-label="Pagination"
                className="flex items-center justify-between gap-4 text-sm"
              >
                <Button
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <span className="text-hull-400">
                  Page {page} of {pages}
                </span>
                <Button
                  variant="secondary"
                  disabled={page >= pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </AdminPanelState>

      {selected && <UserDrawer userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/**
 * One customer, with the suspend/reinstate control.
 *
 * A panel rather than a route so the operator does not lose their place in a
 * filtered, paginated list to read one account and come back.
 */
function UserDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  /**
   * Escape closes, and opening moves focus into the panel.
   *
   * Both are what `aria-modal="true"` promises. Without the first, a keyboard
   * user has to tab to the close button past everything in the panel; without
   * the second, focus is left on the "View" link behind an overlay that has
   * covered it.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onKeyDown);
    panel.current?.focus();

    // The page behind must not scroll while the panel is over it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const detail = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => adminApi.getUser(userId),
  });

  const change = useMutation({
    mutationFn: (next: 'suspended' | 'active') =>
      adminApi.setUserStatus(userId, next, reason.trim() || null),
    onSuccess: (updated) => {
      setError(null);
      setReason('');
      queryClient.setQueryData(['admin', 'user', userId], updated);
      // The list shows status, and the dashboard counts it.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
    onError: (cause) => {
      setError(cause instanceof ApiError ? cause.message : 'That change did not go through.');
    },
  });

  const user: AdminUserRow | undefined = detail.data?.user;
  const suspended = user?.status === 'suspended';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-hull-950/70 backdrop-blur-sm">
      <button
        type="button"
        className="flex-1 cursor-default"
        onClick={onClose}
        aria-label="Close panel"
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Customer account"
        tabIndex={-1}
        className="w-full max-w-lg overflow-y-auto border-l border-hull-700 bg-hull-900 p-6 outline-none"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-white">
              {user?.display_name ?? user?.email ?? 'Account'}
            </h2>
            {user && <p className="truncate text-sm text-hull-400">{user.email}</p>}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>

        <AdminPanelState loading={detail.isPending} error={detail.error}>
          {detail.data && user && (
            <div className="space-y-6">
              {error && <Alert tone="error">{error}</Alert>}

              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-xs text-hull-400">Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={user.status} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Plan</dt>
                  <dd className="mt-1 capitalize text-hull-200">{user.plan ?? 'none'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Joined</dt>
                  <dd className="mt-1 text-hull-200">{formatDate(user.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Last sign-in</dt>
                  <dd className="mt-1 text-hull-200">{formatDateTime(user.last_login_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Lifetime revenue</dt>
                  <dd className="mt-1 tabular-nums text-hull-200">
                    {formatMoney(user.revenue_minor_units, detail.data.currency, {
                      exact: true,
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">QA runs</dt>
                  <dd className="mt-1 tabular-nums text-hull-200">
                    {formatCount(detail.data.qa_runs)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Agent credentials</dt>
                  <dd className="mt-1 tabular-nums text-hull-200">
                    {detail.data.credentials_active} active of {detail.data.credentials_total}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-hull-400">Email verified</dt>
                  <dd className="mt-1 text-hull-200">
                    {formatDate(detail.data.email_verified_at)}
                  </dd>
                </div>
              </dl>

              {detail.data.subscription && (
                <section className="rounded-card border border-hull-700 bg-hull-800/60 p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-hull-400">
                    Subscription
                  </h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-hull-400">State</dt>
                      <dd className="capitalize text-hull-200">
                        {detail.data.subscription.status}
                        {!detail.data.subscription.is_entitled && (
                          <span className="ml-2 text-xs text-fail-400">not entitled</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-hull-400">Hours used</dt>
                      <dd className="tabular-nums text-hull-200">
                        {detail.data.subscription.hours_used_this_period} of{' '}
                        {detail.data.subscription.monthly_hour_allowance}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-hull-400">Criteria used</dt>
                      <dd className="tabular-nums text-hull-200">
                        {detail.data.subscription.criteria_used_this_period} of{' '}
                        {detail.data.subscription.monthly_criteria_allowance ?? 'unlimited'}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-hull-400">Period ends</dt>
                      <dd className="text-hull-200">
                        {formatDate(detail.data.subscription.current_period_end)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-hull-400">
                        {detail.data.subscription.promotion_active
                          ? 'Promotion ends'
                          : 'Promotion'}
                      </dt>
                      <dd className="text-hull-200">
                        {detail.data.subscription.promotion_ends_at === null ? (
                          <span className="text-hull-500">never enrolled</span>
                        ) : detail.data.subscription.promotion_active ? (
                          formatDate(detail.data.subscription.promotion_ends_at)
                        ) : (
                          <span className="text-hull-300">
                            ended {formatDate(detail.data.subscription.promotion_ends_at)}
                          </span>
                        )}
                        {detail.data.subscription.promotion_grants > 1 && (
                          <span className="ml-2 text-xs text-hull-400">
                            ({detail.data.subscription.promotion_grants} granted)
                          </span>
                        )}
                      </dd>
                    </div>
                    {detail.data.subscription.requires_payment && (
                      <div className="flex justify-between gap-4">
                        <dt className="text-hull-400">Payment</dt>
                        {/* The state the promotion is designed to end in, and
                            the one an operator is most likely to be looking at
                            this account because of. Granting another window is
                            on the Promotions screen, not here: it is a bulk
                            decision and this panel is one account. */}
                        <dd className="text-warn-400">needed to keep running</dd>
                      </div>
                    )}
                  </dl>
                </section>
              )}

              <section className="rounded-card border border-hull-700 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-hull-400">
                  {suspended ? 'Reinstate account' : 'Suspend account'}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-hull-400">
                  {suspended
                    ? 'The customer can sign in and run the agent again from their next request.'
                    : 'Blocks sign-in and every agent operation from the next request. The customer is not emailed.'}
                </p>

                {!suspended && (
                  <div className="mt-3">
                    <label htmlFor="suspend-reason" className="mb-1.5 block text-xs text-hull-400">
                      Reason (recorded in the audit log)
                    </label>
                    <Input
                      id="suspend-reason"
                      value={reason}
                      maxLength={256}
                      placeholder="e.g. chargeback under investigation"
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </div>
                )}

                <Button
                  variant={suspended ? 'primary' : 'secondary'}
                  className="mt-4"
                  disabled={change.isPending}
                  onClick={() => change.mutate(suspended ? 'active' : 'suspended')}
                >
                  {change.isPending
                    ? 'Saving…'
                    : suspended
                      ? 'Reinstate account'
                      : 'Suspend account'}
                </Button>
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-hull-400">
                  Recent activity
                </h3>
                {detail.data.recent_events.length === 0 ? (
                  <p className="mt-2 text-sm text-hull-500">Nothing recorded yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2 text-xs">
                    {detail.data.recent_events.map((event) => (
                      <li
                        key={event.id}
                        className="flex items-start justify-between gap-3 border-b border-hull-800 pb-2"
                      >
                        <span className="min-w-0">
                          <span
                            className={cx(
                              'block font-medium',
                              event.succeeded ? 'text-hull-200' : 'text-fail-400',
                            )}
                          >
                            {event.event_type.replace(/_/g, ' ').toLowerCase()}
                          </span>
                          {event.reason && (
                            <span className="block text-hull-500">{event.reason}</span>
                          )}
                          {event.actor_admin_email && (
                            <span className="block text-hull-500">
                              by {event.actor_admin_email}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 whitespace-nowrap text-hull-500">
                          {formatDateTime(event.occurred_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </AdminPanelState>
      </div>
    </div>
  );
}
